import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Rating,
  Stack,
  IconButton,
  Paper,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  FavoriteBorder as FavoriteBorderIcon,
  LocalOffer as OfferIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchFeaturedProducts } from '../store/slices/productSlice';
import { addItemLocally } from '../store/slices/cartSlice';
import { setTempAdminRole } from '../utils/adminUtils';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { featuredProducts, isLoading } = useSelector((state: RootState) => state.product);

  useEffect(() => {
    dispatch(fetchFeaturedProducts());
  }, [dispatch]);

  const handleAddToCart = (product: any) => {
    dispatch(addItemLocally({ product, quantity: 1 }));
  };

  return (
    <Box>
      {/* Hero Banner Section - Ohou Style */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #35C5F0 0%, #63D3F5 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={4}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '3rem' },
                  mb: 2,
                  lineHeight: 1.2,
                }}
              >
                Create Your Perfect<br />Space
              </Typography>
              <Typography 
                variant="h5" 
                component="p" 
                sx={{ 
                  mb: 4, 
                  opacity: 0.95,
                  fontWeight: 400,
                  lineHeight: 1.6,
                }}
              >
                Complete your special interior with Dewfoam,<br />
                a premium furniture brand with 25 years of tradition
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/products')}
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Shop All Products
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/products?category=living-room')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      borderColor: 'white',
                    },
                  }}
                >
                  Popular Categories
                </Button>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600"
                alt="Dewfoam Interior"
                sx={{
                  width: '100%',
                  height: { xs: 300, md: 400 },
                  objectFit: 'cover',
                  borderRadius: 3,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
              />
            </Grid>
          </Grid>
        </Container>
        <Box
          sx={{
            position: 'absolute',
            bottom: -50,
            right: -50,
            width: 200,
            height: 200,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            zIndex: 0,
          }}
        />
      </Box>

      {/* Special Offers Banner */}
      <Box sx={{ backgroundColor: 'secondary.main', color: 'white', py: 2 }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
            <OfferIcon />
            <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
              New Year Special Sale! Up to 30% off all products
            </Typography>
            <Button
              variant="outlined"
              size="small"
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white',
                },
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Categories Section - Modern Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Shop by Category
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
            Find the perfect furniture for every space
          </Typography>
        </Box>
        
        <Grid container spacing={3}>
          {[
            { 
              name: 'Living Room', 
              slug: 'living-room', 
              image: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400',
              description: 'Comfortable relaxation space'
            },
            { 
              name: 'Bedroom', 
              slug: 'bedroom', 
              image: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=400',
              description: 'Cozy sleeping space'
            },
            { 
              name: 'Dining', 
              slug: 'dining-room', 
              image: 'https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=400',
              description: 'Shared dining space'
            },
            { 
              name: 'Office', 
              slug: 'office', 
              image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400',
              description: 'Efficient workspace'
            },
          ].map((category) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={category.slug}>
              <Card
                sx={{
                  height: 280,
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    '& .category-overlay': {
                      backgroundColor: 'rgba(53, 197, 240, 0.8)',
                    },
                    '& .category-content': {
                      transform: 'translateY(-10px)',
                    },
                  },
                }}
                onClick={() => navigate(`/products?category=${category.slug}`)}
              >
                <CardMedia
                  component="img"
                  height="280"
                  image={category.image}
                  alt={category.name}
                  sx={{ objectFit: 'cover' }}
                />
                <Box
                  className="category-overlay"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box
                    className="category-content"
                    sx={{
                      textAlign: 'center',
                      color: 'white',
                      transition: 'transform 0.3s ease',
                    }}
                  >
                    <Typography variant="h4" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                      {category.name}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      {category.description}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Featured Products Section */}
      <Box sx={{ backgroundColor: '#FAFBFC', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6 }}>
            <Box>
              <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                This Week's Popular Products
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Best-sellers that customers love most
              </Typography>
            </Box>
            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/products')}
              sx={{
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.dark',
                },
              }}
            >
              View All
            </Button>
          </Box>

          <Grid container spacing={3}>
            {featuredProducts.map((product) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      '& .product-image': {
                        transform: 'scale(1.05)',
                      },
                    },
                  }}
                >
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      className="product-image"
                      component="img"
                      height="260"
                      image={product.imageUrl || 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400'}
                      alt={product.name}
                      sx={{ 
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'white',
                        color: 'grey.600',
                        '&:hover': {
                          backgroundColor: 'white',
                          color: 'secondary.main',
                        },
                      }}
                    >
                      <FavoriteBorderIcon />
                    </IconButton>
                    {product.comparePrice && (
                      <Chip
                        label={`${Math.round((1 - product.price / product.comparePrice) * 100)}% OFF`}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          backgroundColor: 'secondary.main',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography 
                      variant="h6" 
                      component="h3" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        height: 40,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {product.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        ₱{product.price.toLocaleString()}
                      </Typography>
                      {product.comparePrice && (
                        <Typography
                          variant="body2"
                          sx={{ 
                            textDecoration: 'line-through', 
                            color: 'grey.500',
                            fontSize: '0.9rem',
                          }}
                        >
                          ₱{product.comparePrice.toLocaleString()}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      <Rating value={4.5} precision={0.5} size="small" readOnly />
                      <Typography variant="body2" color="text.secondary">
                        (124)
                      </Typography>
                    </Stack>
                  </CardContent>
                  
                  <CardActions sx={{ p: 3, pt: 0, gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate(`/products/${product.slug}`)}
                      sx={{
                        py: 1,
                        fontWeight: 600,
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleAddToCart(product)}
                      sx={{
                        py: 1,
                        fontWeight: 600,
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                      }}
                    >
                      Add to Cart
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {featuredProducts.length === 0 && !isLoading && (
            <Paper sx={{ p: 6, textAlign: 'center', backgroundColor: 'white' }}>
              <Typography variant="h6" color="text.secondary">
                No products available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                New products will be available soon
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Brand Story Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ pr: { md: 4 } }}>
              <Chip 
                label="ABOUT DEWFOAM" 
                sx={{ 
                  mb: 3, 
                  backgroundColor: 'primary.light',
                  color: 'primary.dark',
                  fontWeight: 600,
                }}
              />
              <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 3 }}>
                Premium Furniture Brand<br />with 25 Years of Tradition
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  color: 'text.secondary',
                }}
              >
                Since 1999, Dewfoam has established itself as the Philippines' leading furniture manufacturing brand,
                showcasing premium furniture that combines traditional craftsmanship with modern design.
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  color: 'text.secondary',
                }}
              >
                Headquartered in Metro Manila and serving nationwide,
                we complete every space with our diverse lineup spanning
                living room, bedroom, dining room, and office furniture.
              </Typography>
              
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/products')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Explore Collection
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Brand Story
                </Button>
              </Stack>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600"
                alt="Dewfoam Showroom"
                sx={{
                  width: '100%',
                  height: 500,
                  objectFit: 'cover',
                  borderRadius: 3,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              />
              <Paper
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  minWidth: 200,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <TrendingUpIcon color="primary" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      10,000+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Satisfied Customers
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Development Admin Access (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <Button
            variant="contained"
            color="error"
            onClick={setTempAdminRole}
            sx={{
              backgroundColor: '#FF6B6B',
              '&:hover': { backgroundColor: '#E85A5A' },
              fontWeight: 600,
              borderRadius: 3,
              px: 3,
              py: 1.5,
              boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
            }}
          >
            🔧 DEV: Admin Access
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HomePage;